import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import WaSwitch from '../../components/wa/WaSwitch';
import { db } from '../../firebase';
import {
	collection,
	query,
	where,
	getDocs,
	updateDoc,
	doc,
} from 'firebase/firestore';

// The four capability switches established in Phase E1. Keys must NOT be
// renamed — firestore.rules' hasPerm() reads them verbatim. `note` flags a
// switch whose downstream feature isn't built yet (toggle is allowed but
// currently inert until a later phase).
const PERMISSIONS = [
	{
		key: 'viewStudents',
		label: 'See all students',
		help: 'Access the full student roster and profiles.',
	},
	{
		key: 'viewSchedule',
		label: 'See the schedule',
		help: 'View the teaching schedule and lesson calendar.',
	},
	{
		key: 'manageTasks',
		label: 'Use the task board',
		help: 'Work assigned tasks and file tickets.',
	},
	{
		key: 'sendEmail',
		label: 'Email families',
		help: 'Compose and send email to families from the portal.',
	},
];

const TEACHING_ARTIST_PRESET = ['viewStudents', 'viewSchedule', 'manageTasks'];

const NAVY = '#384971';

// No name fields live on the users doc today — derive a friendly display name
// from the email local-part, falling back to the raw email.
const displayName = (email) => {
	if (!email) return 'Unknown user';
	const local = email.split('@')[0];
	return local
		.split(/[._-]+/)
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

const StaffAccess = () => {
	const { userProfile } = useAuth();
	const [staff, setStaff] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	// uid -> 'saving' | 'saved' | 'error'
	const [saveState, setSaveState] = useState({});

	const isAdmin = userProfile?.role === 'admin';

	const loadStaff = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const q = query(
				collection(db, 'users'),
				where('role', 'in', ['employee', 'admin'])
			);
			const snap = await getDocs(q);
			const rows = snap.docs.map((d) => ({
				uid: d.id,
				email: d.data().email || '',
				role: d.data().role,
				owner: d.data().owner === true,
				permissions: d.data().permissions || {},
			}));
			rows.sort((a, b) => a.email.localeCompare(b.email));
			setStaff(rows);
		} catch (err) {
			console.error('Error loading staff:', err);
			setLoadError(err.message || 'Failed to load staff.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (isAdmin) loadStaff();
	}, [isAdmin, loadStaff]);

	const flashSaved = (uid) => {
		setSaveState((prev) => ({ ...prev, [uid]: 'saved' }));
		setTimeout(() => {
			setSaveState((prev) => {
				if (prev[uid] !== 'saved') return prev;
				const next = { ...prev };
				delete next[uid];
				return next;
			});
		}, 2000);
	};

	// Optimistically apply a permissions patch to local state, write it, and
	// roll back on error. `patch` is a partial permissions map.
	const writePermissions = async (uid, patch) => {
		const target = staff.find((s) => s.uid === uid);
		if (!target) return;
		const prevPermissions = target.permissions;
		const nextPermissions = { ...prevPermissions, ...patch };

		setStaff((rows) =>
			rows.map((s) =>
				s.uid === uid ? { ...s, permissions: nextPermissions } : s
			)
		);
		setSaveState((prev) => ({ ...prev, [uid]: 'saving' }));

		try {
			// Dot-path update keeps it a targeted merge into the permissions map.
			const dotPatch = {};
			Object.entries(patch).forEach(([k, v]) => {
				dotPatch[`permissions.${k}`] = v;
			});
			await updateDoc(doc(db, 'users', uid), dotPatch);
			flashSaved(uid);
		} catch (err) {
			console.error('Error saving permissions:', err);
			// Roll back optimistic update.
			setStaff((rows) =>
				rows.map((s) =>
					s.uid === uid ? { ...s, permissions: prevPermissions } : s
				)
			);
			setSaveState((prev) => ({ ...prev, [uid]: 'error' }));
		}
	};

	const handleToggle = (uid, key, checked) => {
		writePermissions(uid, { [key]: checked });
	};

	const handlePreset = (uid) => {
		const patch = {};
		TEACHING_ARTIST_PRESET.forEach((k) => {
			patch[k] = true;
		});
		writePermissions(uid, patch);
	};

	if (!isAdmin) {
		return (
			<div className="container py-5">
				<wa-callout variant="danger">
					<strong>Access Denied</strong>
					<p className="mb-0 mt-2">
						You must be an administrator to access this page.
					</p>
				</wa-callout>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="container py-5">
				<div className="text-center">
					<wa-spinner style={{ fontSize: '2rem' }}></wa-spinner>
				</div>
			</div>
		);
	}

	const owners = staff.filter((s) => s.role === 'admin' || s.owner);
	const teachers = staff.filter((s) => s.role === 'employee' && !s.owner);

	const renderSaveStatus = (uid) => {
		const state = saveState[uid];
		if (state === 'saving') {
			return (
				<span className="text-muted" style={{ fontSize: '0.85rem' }}>
					<wa-spinner style={{ fontSize: '0.85rem' }}></wa-spinner> Saving…
				</span>
			);
		}
		if (state === 'saved') {
			return (
				<span style={{ color: '#2e7d32', fontSize: '0.85rem' }}>
					<i className="fa-sharp fa-solid fa-check me-1"></i>Saved
				</span>
			);
		}
		if (state === 'error') {
			return (
				<span style={{ color: '#c62828', fontSize: '0.85rem' }}>
					<i className="fa-sharp fa-solid fa-triangle-exclamation me-1"></i>
					Couldn't save
				</span>
			);
		}
		return null;
	};

	return (
		<div className="container py-5">
			<h1 className="mb-2" style={{ color: NAVY }}>
				Staff Access
			</h1>
			<p className="text-muted mb-5">
				Grant or revoke what each teacher can do in the portal. Owners always
				have full access.
			</p>

			{loadError && (
				<wa-callout variant="danger" className="mb-4">
					<strong>Couldn't load staff.</strong>
					<p className="mb-0 mt-2">{loadError}</p>
				</wa-callout>
			)}

			{/* Owners / admins — read-only full access */}
			<h2 className="mb-3" style={{ color: NAVY, fontSize: '1.4rem' }}>
				Owners
			</h2>
			<div className="grid grid-cols-1 grid-cols-md-2 gap-4 mb-5">
				{owners.map((person) => (
					<wa-card key={person.uid}>
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<div style={{ fontWeight: 600, color: NAVY }}>
									{displayName(person.email)}
								</div>
								<div className="text-muted" style={{ fontSize: '0.85rem' }}>
									{person.email}
								</div>
							</div>
							<wa-badge variant="success" pill>
								<i className="fa-sharp fa-solid fa-shield-check me-1"></i>
								Full access
							</wa-badge>
						</div>
					</wa-card>
				))}
			</div>

			{/* Non-admin employees — editable toggles */}
			<h2 className="mb-3" style={{ color: NAVY, fontSize: '1.4rem' }}>
				Teachers
			</h2>

			{teachers.length === 0 ? (
				<wa-callout variant="neutral">
					<i className="fa-sharp fa-solid fa-user-clock me-2"></i>
					No teachers yet — they'll appear here after their first sign-in.
				</wa-callout>
			) : (
				<div className="d-flex flex-column gap-4">
					{teachers.map((person) => {
						const perms = person.permissions || {};
						return (
							<wa-card key={person.uid}>
								<div
									slot="header"
									className="d-flex justify-content-between align-items-center flex-wrap gap-2">
									<div>
										<strong style={{ color: NAVY }}>
											{displayName(person.email)}
										</strong>
										<div
											className="text-muted"
											style={{ fontSize: '0.85rem' }}>
											{person.email}
										</div>
									</div>
									<div className="d-flex align-items-center gap-3">
										{renderSaveStatus(person.uid)}
										<wa-button
											size="small"
											variant="brand"
											onClick={() => handlePreset(person.uid)}>
											<i className="fa-sharp fa-solid fa-wand-magic-sparkles me-1"></i>
											Teaching Artist
										</wa-button>
									</div>
								</div>

								<div className="d-flex flex-column gap-3">
									{PERMISSIONS.map((p) => (
										<div
											key={p.key}
											className="d-flex justify-content-between align-items-center gap-3">
											<div>
												<div style={{ fontWeight: 500 }}>
													{p.label}
													{p.note && (
														<wa-badge
															variant="neutral"
															className="ms-2"
															pill
															style={{ fontSize: '0.7rem' }}>
															{p.note}
														</wa-badge>
													)}
												</div>
												<div
													className="text-muted"
													style={{ fontSize: '0.8rem' }}>
													{p.help}
												</div>
											</div>
											<WaSwitch
												checked={perms[p.key] === true}
												onToggle={(checked) =>
													handleToggle(
														person.uid,
														p.key,
														checked
													)
												}
											/>
										</div>
									))}
								</div>
							</wa-card>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default StaffAccess;
